import Vue from 'vue'
import { getResourceDailySyncList } from '@/api/api'

const RECENT_DOWNLOAD_WORKER_COMPONENTS = {
  paper: 'PaperSync',
  meeting: 'MeetingSync',
  learning: 'LearningSync',
  knowledge: 'KnowledgeBaseSync',
  memo: 'MemoSync',
  daily: 'DailySync'
}

export default {
  methods: {
    getRecentDownloadWorkerComponent(item) {
      const folderType = this.resolveAppFolderType(item) || (this.isDirectFilePreviewItem(item) ? 'paper' : '')
      const componentName = RECENT_DOWNLOAD_WORKER_COMPONENTS[folderType]
      return componentName && this.$options.components
        ? this.$options.components[componentName]
        : null
    },
    createRecentDownloadWorker(item) {
      const component = this.getRecentDownloadWorkerComponent(item)
      if (!component) {
        return null
      }
      const folderType = this.resolveAppFolderType(item) || (this.isDirectFilePreviewItem(item) ? 'paper' : '')
      const propsData = {
        language: this.selectedLanguage
      }
      if (['learning', 'memo', 'daily'].includes(folderType)) {
        propsData.appType = item && item.appType ? item.appType : this.resolveDefaultAppTypeByFolderType(folderType)
      }
      if (folderType == 'learning') {
        propsData.folderType = 'learning'
      }
      if (folderType == 'daily') {
        const dailyTab = this.resolveRecentDailyTab(item)
        propsData.folderType = 'daily'
        propsData.initialActiveTab = dailyTab == 'todo' ? 'tasks' : dailyTab
        propsData.skipInitialDashboardLoad = true
      }
      const WorkerConstructor = Vue.extend(component)
      return new WorkerConstructor({
        parent: this,
        propsData
      })
    },
    destroyRecentDownloadWorker(worker) {
      if (worker && typeof worker.$destroy == 'function') {
        worker.$destroy()
      }
    },
    createRecentDownloadWorkerSource(item) {
      const source = Object.assign({}, item && item.raw ? item.raw : {}, {
        resourceId: item && item.resourceId,
        name: item && item.name,
        cover: item && item.cover,
        filePath: item && item.filePath,
        childSize: item && item.childSize,
        resourceType: item && item.resourceType,
        subTab: item && item.subTab,
        appType: item && item.appType,
        encrypted: item && item.encrypted
      })
      if (item && item.createdAt) {
        source.createdAt = item.createdAt
      }
      if (item && item.updatedAt) {
        source.updatedAt = item.updatedAt
      }
      if (this.resolveAppFolderType(item) == 'daily') {
        const dailyDate = this.resolveRecentDailyDate(item)
        const dailyResourceId = this.resolveRecentDailyPreviewResourceId(item)
        if (dailyDate) {
          source.date = dailyDate
          source.syncDate = dailyDate
        }
        if (dailyResourceId) {
          source.resourceId = dailyResourceId
          source.dailyId = dailyResourceId
        }
      }
      return source
    },
    applyRecentDownloadWorkerTask(task, workerTask) {
      Object.keys(workerTask || {}).forEach(key => {
        if (['id', 'sourceItem', 'password'].includes(key)) {
          return
        }
        this.$set(task, key, workerTask[key])
      })
    },
    throwRecentDownloadWorkerError(task) {
      if (!task || task.state != 'error') {
        return
      }
      const error = new Error(task.statusText || 'Download failed')
      error.isCorsLikeDownloadError = String(task.statusText || '').toLowerCase().includes('image')
      throw error
    },
    async downloadRecentItemWithBusinessWorker(task, item, format) {
      const worker = this.createRecentDownloadWorker(item)
      if (!worker) {
        return false
      }
      try {
        if (this.resolveAppFolderType(item) == 'daily') {
          await this.downloadRecentDailyWithBusinessWorker(worker, task, item, format)
          this.throwRecentDownloadWorkerError(task)
          return true
        }
        if (typeof worker.normalizePaperItem != 'function' || typeof worker.createPaperDownloadTask != 'function' || typeof worker.runPaperDownloadTask != 'function') {
          return false
        }
        const source = this.createRecentDownloadWorkerSource(item)
        const normalizedItem = worker.normalizePaperItem(source, 0)
        this.$set(normalizedItem, 'disableDownloadPageFallback', true)
        if (!task.delegatedDownloadInitialized) {
          const workerTask = worker.createPaperDownloadTask(normalizedItem, format, task.password || '')
          this.applyRecentDownloadWorkerTask(task, workerTask)
          this.$set(task, 'delegatedDownloadInitialized', true)
        }
        await worker.runPaperDownloadTask(task, normalizedItem, format)
        this.throwRecentDownloadWorkerError(task)
        return true
      } finally {
        this.destroyRecentDownloadWorker(worker)
      }
    },
    async downloadRecentDailyWithBusinessWorker(worker, task, item, format) {
      const dailyTab = this.resolveRecentDailyTab(item)
      worker.dailyActiveTab = dailyTab == 'todo' ? 'tasks' : dailyTab
      const source = this.createRecentDownloadWorkerSource(item)
      const normalizedItem = worker.normalizeDailyItem(source, 0)
      const groups = worker.groupDailyItemsByName([normalizedItem])
      const group = groups[0] || {
        name: item && item.name ? item.name : 'Daily',
        items: [normalizedItem],
        previewRequest: worker.buildDailyPreviewRequestFromItem(normalizedItem)
      }
      const previewRequest = worker.buildDailyPreviewRequestParams(group)
      if (!previewRequest) {
        throw new Error('No pages')
      }
      const requestParams = Object.assign({}, previewRequest)
      if (task && task.password) {
        requestParams.password = task.password
      }
      let requestController = null
      let ret = null
      try {
        if (task && typeof this.ensurePaperDownloadTaskActive == 'function') {
          this.ensurePaperDownloadTaskActive(task)
        }
        if (task && typeof AbortController !== 'undefined') {
          requestController = new AbortController()
          if (typeof this.registerPreviewPerformanceTaskAbortController == 'function') {
            this.registerPreviewPerformanceTaskAbortController(task, requestController)
          } else if (typeof this.$set == 'function') {
            this.$set(task, 'abortController', requestController)
          } else {
            task.abortController = requestController
          }
        }
        ret = await getResourceDailySyncList(requestParams, {
          skipGlobalErrorMessage: true,
          signal: requestController ? requestController.signal : undefined
        })
        if (task && typeof this.ensurePaperDownloadTaskActive == 'function') {
          this.ensurePaperDownloadTaskActive(task)
        }
      } catch (error) {
        if (error && error.isPaperDownloadCancelled) {
          throw error
        }
        if (task && (task.cancelled || error && (error.name == 'AbortError' || error.name == 'CanceledError' || error.code == 'ERR_CANCELED'))) {
          const cancelError = new Error('Download cancelled')
          cancelError.isPaperDownloadCancelled = true
          throw cancelError
        }
        throw error
      } finally {
        if (task && requestController) {
          if (typeof this.unregisterPreviewPerformanceTaskAbortController == 'function') {
            this.unregisterPreviewPerformanceTaskAbortController(task, requestController)
          } else if (task.abortController === requestController) {
            if (typeof this.$set == 'function') {
              this.$set(task, 'abortController', null)
            } else {
              task.abortController = null
            }
          }
        }
      }
      const payload = worker.resolveApiPayload(ret)
      const rawItems = payload === null ? [] : worker.extractArray(payload)
      const items = rawItems.map((page, index) => worker.normalizeDailyItem(page, index))
      const pages = (Array.isArray(items) ? items : [])
        .map((page, index) => worker.normalizeDailyDownloadPageForPaper(page, index, group.name))
        .filter(page => page && (page.imageUrl || page.pageImageUrl))
      if (pages.length == 0) {
        throw new Error('No pages')
      }
      if (!task.delegatedDownloadInitialized) {
        const workerTask = worker.createDailyDetailPagesDownloadTask(pages, group.name, format)
        this.applyRecentDownloadWorkerTask(task, workerTask)
        this.$set(task, 'delegatedDownloadInitialized', true)
      } else {
        this.$set(task, 'pages', pages)
      }
      await worker.runDailyDetailPagesDownloadTask(task)
    }
  }
}
